import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Brain, ChevronDown, ChevronUp } from 'lucide-react';
import { io } from 'socket.io-client';
import WhiteboardCanvas from '../components/whiteboard/WhiteboardCanvas';
import WhiteboardSidebar from '../components/whiteboard/WhiteboardSidebar';
import SessionOpenerPanel from '../components/whiteboard/SessionOpenerPanel';
import MetacognitiveWrapup from '../components/whiteboard/MetacognitiveWrapup';
import HelperIcon from '../components/HelperIcon';
import { helperContent } from '../content/helper-content';
import { useWorkflow } from '../context/WorkflowContext';
import { apiAIFetch, apiFetch, getAuthToken, getStoredUser } from '../api';
import { API_BASE } from '../config';

const AUTOSAVE_DELAY_MS = 300;
const IMMEDIATE_AUTOSAVE_DELAY_MS = 50;
const HYDRATION_SUPPRESS_MS = 600;
const ACTIVE_SYNC_POLL_INTERVAL_MS = 1500;
const IDLE_SYNC_POLL_INTERVAL_MS = 5000;
const RECENT_ACTIVITY_SYNC_WINDOW_MS = 15000;
const SOCKET_UPDATE_TIMEOUT_MS = 6000;
const DEFAULT_RATE_LIMIT_BACKOFF_MS = 15000;
const SOCKET_FALLBACK_GRACE_MS = 3000;
const LAST_WHITEBOARD_KEY = 'lrbrain-whiteboard-last-selected-id';
const WHITEBOARD_CLIENT_ID_KEY = 'lrbrain-whiteboard-client-id';
const EMPTY_SNAPSHOT = {
  version: 4,
  paths: [],
  elements: [],
  shapes: [],
  _tombstones: [],
  viewport: { zoom: 1, panX: 0, panY: 0 }
};

const safeStringify = (value, space = 0) => {
  const seen = new WeakSet();
  return JSON.stringify(
    value,
    (key, current) => {
      if (typeof current === 'function' || typeof current === 'symbol') {
        return undefined;
      }
      if (typeof current === 'bigint') {
        return Number(current);
      }
      if (current && typeof current === 'object') {
        if (seen.has(current)) {
          return undefined;
        }
        seen.add(current);
      }
      return current;
    },
    space
  );
};

const snapshotToString = (snapshot, space = 0) => {
  try {
    return safeStringify(snapshot, space);
  } catch {
    return safeStringify(EMPTY_SNAPSHOT, space);
  }
};

const isBoardEmpty = (snapshot) => {
  if (!snapshot) return true;
  const paths = Array.isArray(snapshot.paths) ? snapshot.paths : [];
  const shapes = Array.isArray(snapshot.shapes) ? snapshot.shapes : [];
  const elements = Array.isArray(snapshot.elements) ? snapshot.elements : [];
  return paths.length === 0 && shapes.length === 0 && elements.length === 0;
};

const formatDefaultWhiteboardTitle = (date = new Date()) => {
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
  return formattedDate;
};

const parseUpdatedAtMs = (value) => {
  const ms = Date.parse(value || '');
  return Number.isFinite(ms) ? ms : 0;
};

const pickMostRecentBoardId = (boards) => {
  if (!Array.isArray(boards) || boards.length === 0) return null;
  const sorted = [...boards].sort((a, b) => {
    const bMs = parseUpdatedAtMs(b?.updated_at);
    const aMs = parseUpdatedAtMs(a?.updated_at);
    if (bMs !== aMs) return bMs - aMs;
    return 0;
  });
  return sorted[0]?.id || null;
};

const readCachedWhiteboardId = () => {
  try {
    const persisted = localStorage.getItem(LAST_WHITEBOARD_KEY);
    if (persisted) return persisted;
    const legacy = sessionStorage.getItem(LAST_WHITEBOARD_KEY);
    if (legacy) {
      localStorage.setItem(LAST_WHITEBOARD_KEY, legacy);
      return legacy;
    }
    return null;
  } catch {
    return null;
  }
};

const writeCachedWhiteboardId = (id) => {
  try {
    if (id) {
      localStorage.setItem(LAST_WHITEBOARD_KEY, String(id));
      sessionStorage.setItem(LAST_WHITEBOARD_KEY, String(id));
      return;
    }
    localStorage.removeItem(LAST_WHITEBOARD_KEY);
    sessionStorage.removeItem(LAST_WHITEBOARD_KEY);
  } catch {}
};

const getOrCreateWhiteboardClientId = () => {
  try {
    const existing = sessionStorage.getItem(WHITEBOARD_CLIENT_ID_KEY);
    if (existing) return existing;
    const generated = `wb-client-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(WHITEBOARD_CLIENT_ID_KEY, generated);
    return generated;
  } catch {
    return `wb-client-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
};

const getWhiteboardDisplayLabel = (isPublicShare) => {
  if (isPublicShare) return '';
  const user = getStoredUser();
  const name = user?.display_name || user?.name || user?.email || '';
  if (!name) return 'Teacher';
  return String(name).split('@')[0].trim() || 'Teacher';
};

const resolveSocketBaseUrl = () => {
  if (API_BASE && API_BASE.trim()) return API_BASE.trim();
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
};

const Whiteboard = ({ publicShare = false }) => {
  const [searchParams] = useSearchParams();
  const { token: publicShareToken = '' } = useParams();
  const { studentContext } = useWorkflow();
  const isPublicShare = Boolean(publicShare);
  const [whiteboards, setWhiteboards] = useState([]);
  const [hasLoadedWhiteboards, setHasLoadedWhiteboards] = useState(isPublicShare);
  const [selectedId, setSelectedId] = useState(null);
  const [currentRevision, setCurrentRevision] = useState(0);
  const [currentSnapshot, setCurrentSnapshot] = useState(EMPTY_SNAPSHOT);
  const [currentTitle, setCurrentTitle] = useState(formatDefaultWhiteboardTitle());
  const [isLoading, setIsLoading] = useState(false);
  const [hasHydratedInitialBoard, setHasHydratedInitialBoard] = useState(isPublicShare);
  const [isSaving, setIsSaving] = useState(false);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaveError, setLastSaveError] = useState(null);
  const [switchActionModal, setSwitchActionModal] = useState({
    isOpen: false,
    actionType: null,
    targetId: null
  });
  const [isResolvingSwitchAction, setIsResolvingSwitchAction] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({
    isOpen: false,
    boardId: null
  });
  const [newWhiteboardModal, setNewWhiteboardModal] = useState({
    isOpen: false,
    title: ''
  });
  const [isCreatingWhiteboard, setIsCreatingWhiteboard] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [isShareBusy, setIsShareBusy] = useState(false);
  const [shareStatusText, setShareStatusText] = useState('');
  const [remoteCursors, setRemoteCursors] = useState([]);
  const [collaboratorLabels, setCollaboratorLabels] = useState({});
  const canvasRef = useRef(null);
  const whiteboardClientIdRef = useRef(getOrCreateWhiteboardClientId());
  const displayLabelRef = useRef(getWhiteboardDisplayLabel(isPublicShare));
  const currentRevisionRef = useRef(currentRevision);
  const selectedIdRef = useRef(selectedId);
  const currentTitleRef = useRef(currentTitle);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const lastSavedSnapshotRef = useRef(JSON.stringify(EMPTY_SNAPSHOT));
  const lastSavedTitleRef = useRef(currentTitle);
  const boardFallbackTitleRef = useRef(currentTitle);
  const pendingSaveTimeoutRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const inFlightSavePromiseRef = useRef(null);
  const resolveInFlightSaveRef = useRef(null);
  const needsResaveRef = useRef(false);
  const suppressCanvasChangesUntilRef = useRef(0);
  const hasHydratedInitialBoardRef = useRef(false);
  const syncPollInFlightRef = useRef(false);
  const syncPollBackoffUntilRef = useRef(0);
  const isCanvasInteractingRef = useRef(false);
  const pendingRemoteBoardRef = useRef(null);
  const recentSyncActivityUntilRef = useRef(0);
  const applyPendingRemoteIfReadyRef = useRef(() => false);
  const syncPollingDisabledBoardRef = useRef(null);
  const socketDisconnectedAtRef = useRef(Date.now());

  // Session context state
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  // Injected AI context state (from SessionContextPicker)
  const [injectedContext, setInjectedContext] = useState(null);

  // Session phase state for neuro-informed session structure
  const [sessionPhase, setSessionPhase] = useState('opening');

  // Session opener and wrapup panel state
  const [isOpenerOpen, setIsOpenerOpen] = useState(false);
  const [isWrapupOpen, setIsWrapupOpen] = useState(false);

  // Header collapsed state (visible by default)
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const socketRef = useRef(null);
  const joinedBoardRef = useRef(null);
  const remoteCursorsRef = useRef({});
  const cursorCleanupTimeoutRef = useRef(null);
  const cursorEmitRafRef = useRef(null);
  const pendingCursorRef = useRef(null);
  const collaboratorLabelsRef = useRef({});

  // Content generation state (from sessions panel)
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [generatingSessionId, setGeneratingSessionId] = useState(null);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    currentRevisionRef.current = currentRevision;
  }, [currentRevision]);

  useEffect(() => {
    currentTitleRef.current = currentTitle;
  }, [currentTitle]);

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    collaboratorLabelsRef.current = collaboratorLabels;
  }, [collaboratorLabels]);

  useEffect(() => {
    displayLabelRef.current = getWhiteboardDisplayLabel(isPublicShare);
  }, [isPublicShare]);

  useEffect(() => {
    if (selectedId) {
      setIsSidebarOpen(false);
    }
  }, [selectedId]);

  const clearPendingAutosave = useCallback(() => {
    if (pendingSaveTimeoutRef.current) {
      clearTimeout(pendingSaveTimeoutRef.current);
      pendingSaveTimeoutRef.current = null;
    }
  }, []);

  const getCurrentSnapshot = useCallback(() => {
    return canvasRef.current?.getSnapshot?.() || EMPTY_SNAPSHOT;
  }, []);

  const loadWhiteboards = useCallback(async () => {
    if (isPublicShare) return;
    try {
      const response = await apiFetch('/api/whiteboards');
      if (response.ok) {
        const data = await response.json();
        setWhiteboards(data.whiteboards || []);
      }
    } catch (err) {
      console.error('Error loading whiteboards:', err);
    } finally {
      setHasLoadedWhiteboards(true);
    }
  }, [isPublicShare]);

  const emitSocketUpdate = useCallback(async ({
    boardId,
    title,
    snapshot,
    baseRevision,
    clientId
  }) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected) {
      return null;
    }

    return new Promise((resolve, reject) => {
      let timeoutId = null;
      const done = (result, error) => {
        if (timeoutId) clearTimeout(timeoutId);
        if (error) {
          reject(error);
          return;
        }
        resolve(result || null);
      };

      timeoutId = setTimeout(() => {
        done(null, new Error('Socket update timed out'));
      }, SOCKET_UPDATE_TIMEOUT_MS);

      socket.emit(
        'whiteboard:update',
        {
          board_id: boardId,
          title,
          snapshot,
          base_revision: baseRevision,
          client_id: clientId
        },
        (ack) => {
          if (!ack || ack.ok === false) {
            done(null, new Error(ack?.error || 'Socket update failed'));
            return;
          }
          done(ack, null);
        }
      );
    });
  }, []);

  const persistWhiteboard = useCallback(async ({
    source = 'autosave',
    allowCreate = false,
    refreshList = false
  } = {}) => {
    if (!isPublicShare && !hasHydratedInitialBoardRef.current) {
      return false;
    }
    const boardId = selectedIdRef.current;
    const rawTitle = currentTitleRef.current;
    const hasNonEmptyTitle = typeof rawTitle === 'string' && rawTitle.trim().length > 0;
    const fallbackTitle = boardFallbackTitleRef.current || formatDefaultWhiteboardTitle();
    const title = hasNonEmptyTitle ? rawTitle : fallbackTitle;
    const rawSnapshot = getCurrentSnapshot();
    const snapshotString = snapshotToString(rawSnapshot);
    const snapshot = JSON.parse(snapshotString);
    const boardIsEmpty = isBoardEmpty(snapshot);

    if (source !== 'manual' && boardIsEmpty && !boardId) {
      setHasUnsavedChanges(false);
      hasUnsavedChangesRef.current = false;
      setLastSaveError(null);
      return true;
    }

    if (boardId) {
      const titleChanged = title !== lastSavedTitleRef.current;
      const snapshotChanged = snapshotString !== lastSavedSnapshotRef.current;
      if (!titleChanged && !snapshotChanged) {
        setHasUnsavedChanges(false);
        hasUnsavedChangesRef.current = false;
        setLastSaveError(null);
        return true;
      }
    } else if (!allowCreate) {
      return false;
    }

    if (saveInFlightRef.current) {
      needsResaveRef.current = true;
      const pending = inFlightSavePromiseRef.current;
      if (!pending) return false;
      await pending.catch(() => null);
      if (!hasUnsavedChangesRef.current) {
        return true;
      }
      return persistWhiteboard({ source, allowCreate, refreshList });
    }

    saveInFlightRef.current = true;
    inFlightSavePromiseRef.current = new Promise((resolve) => {
      resolveInFlightSaveRef.current = resolve;
    });
    if (source === 'manual') {
      setIsSaving(true);
    } else {
      setIsAutosaving(true);
    }
    setLastSaveError(null);

    try {
      const saveStartedAt = process.env.NODE_ENV === 'development' && typeof performance !== 'undefined'
        ? performance.now()
        : null;
      let savedBoard = null;
      if (boardId) {
        const socketAck = await emitSocketUpdate({
          boardId,
          title,
          snapshot,
          baseRevision: currentRevisionRef.current,
          clientId: whiteboardClientIdRef.current
        }).catch(() => null);

        if (socketAck?.whiteboard) {
          savedBoard = socketAck.whiteboard;
        } else {
          const updatePath = isPublicShare
            ? `/api/public/whiteboards/share/${publicShareToken}`
            : `/api/whiteboards/${boardId}`;
          const response = await apiFetch(updatePath, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...(isPublicShare ? {} : { title }),
              snapshot,
              base_revision: currentRevisionRef.current,
              client_id: whiteboardClientIdRef.current
            })
          });
          if (!response.ok) {
            if (isPublicShare && response.status === 404) {
              const missingShareError = new Error('Shared whiteboard is unavailable or has been revoked');
              missingShareError.code = 'PUBLIC_SHARE_NOT_FOUND';
              throw missingShareError;
            }
            throw new Error('Failed to update whiteboard');
          }
          const data = await response.json();
          savedBoard = data.whiteboard || null;
        }
      } else {
        if (isPublicShare) {
          throw new Error('Shared whiteboard is not loaded');
        }
        const response = await apiFetch('/api/whiteboards', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title,
            snapshot,
            client_id: whiteboardClientIdRef.current
          })
        });
        if (!response.ok) {
          throw new Error('Failed to create whiteboard');
        }
        const data = await response.json();
        savedBoard = data.whiteboard || null;
      }

      if (!savedBoard) {
        throw new Error(`Failed to ${boardId ? 'update' : 'create'} whiteboard`);
      }
      if (saveStartedAt != null) {
        console.debug('[whiteboard] save latency', {
          source,
          transport: boardId && socketRef.current?.connected ? 'socket' : 'http',
          ms: Math.round(performance.now() - saveStartedAt),
          revision: savedBoard?.revision
        });
      }
      const savedTitle = savedBoard?.title || title;

      if (savedBoard?.id && !boardId) {
        setSelectedId(savedBoard.id);
        selectedIdRef.current = savedBoard.id;
        writeCachedWhiteboardId(savedBoard.id);
      }

      const nextRevision = Number(savedBoard?.revision || 0);
      if (Number.isFinite(nextRevision) && nextRevision > 0) {
        setCurrentRevision(nextRevision);
        currentRevisionRef.current = nextRevision;
      }

      lastSavedSnapshotRef.current = snapshotToString(savedBoard?.snapshot || snapshot);
      lastSavedTitleRef.current = savedTitle;
      boardFallbackTitleRef.current = savedTitle;
      if (currentTitleRef.current !== savedTitle) {
        setCurrentTitle(savedTitle);
        currentTitleRef.current = savedTitle;
      }
      setHasUnsavedChanges(false);
      hasUnsavedChangesRef.current = false;

      if (!isPublicShare && (refreshList || !boardId)) {
        await loadWhiteboards();
      }

      return true;
    } catch (err) {
      console.error('Error saving whiteboard:', err);
      setHasUnsavedChanges(true);
      hasUnsavedChangesRef.current = true;
      setLastSaveError(err?.code === 'PUBLIC_SHARE_NOT_FOUND'
        ? 'Shared whiteboard is unavailable or has been revoked'
        : 'Autosave failed (retrying on next change)');
      if (source === 'manual') {
        alert('Save failed. Please try again.');
      }
      return false;
    } finally {
      if (resolveInFlightSaveRef.current) {
        resolveInFlightSaveRef.current(true);
      }
      if (source === 'manual') {
        setIsSaving(false);
      } else {
        setIsAutosaving(false);
      }
      saveInFlightRef.current = false;
      resolveInFlightSaveRef.current = null;
      inFlightSavePromiseRef.current = null;

      if (needsResaveRef.current) {
        needsResaveRef.current = false;
        if (selectedIdRef.current || !isBoardEmpty(getCurrentSnapshot())) {
          clearPendingAutosave();
          pendingSaveTimeoutRef.current = setTimeout(() => {
            pendingSaveTimeoutRef.current = null;
            void persistWhiteboard({
              source: 'autosave',
              allowCreate: !selectedIdRef.current
            });
          }, AUTOSAVE_DELAY_MS);
        }
      } else {
        window.setTimeout(() => {
          applyPendingRemoteIfReadyRef.current?.();
        }, 0);
      }
    }
  }, [clearPendingAutosave, emitSocketUpdate, getCurrentSnapshot, isPublicShare, loadWhiteboards, publicShareToken]);

  const scheduleAutosave = useCallback(({ immediate = false } = {}) => {
    if (!isPublicShare && !hasHydratedInitialBoardRef.current) return;
    if (Date.now() < suppressCanvasChangesUntilRef.current) return;
    if (isBoardEmpty(getCurrentSnapshot()) && !selectedIdRef.current) return;
    if (isCanvasInteractingRef.current) {
      needsResaveRef.current = true;
      return;
    }
    const allowCreate = !selectedIdRef.current;
    clearPendingAutosave();
    pendingSaveTimeoutRef.current = setTimeout(() => {
      pendingSaveTimeoutRef.current = null;
      void persistWhiteboard({ source: 'autosave', allowCreate });
    }, immediate ? IMMEDIATE_AUTOSAVE_DELAY_MS : AUTOSAVE_DELAY_MS);
  }, [clearPendingAutosave, getCurrentSnapshot, isPublicShare, persistWhiteboard]);

  const markDirty = useCallback((changeType = 'change') => {
    if (!isPublicShare && !hasHydratedInitialBoardRef.current) {
      return;
    }
    if (Date.now() < suppressCanvasChangesUntilRef.current) {
      return;
    }
    recentSyncActivityUntilRef.current = Date.now() + RECENT_ACTIVITY_SYNC_WINDOW_MS;
    if (isBoardEmpty(getCurrentSnapshot()) && !selectedIdRef.current) {
      setHasUnsavedChanges(false);
      hasUnsavedChangesRef.current = false;
      clearPendingAutosave();
      setLastSaveError(null);
      return;
    }
    setHasUnsavedChanges(true);
    hasUnsavedChangesRef.current = true;
    setLastSaveError(null);
    scheduleAutosave({ immediate: changeType === 'discrete' || changeType === 'interaction-end' });
  }, [clearPendingAutosave, getCurrentSnapshot, isPublicShare, scheduleAutosave]);

  const flushPendingSaveIfNeeded = useCallback(async (source = 'autosave') => {
    clearPendingAutosave();
    if (!isPublicShare && !hasHydratedInitialBoardRef.current) {
      return true;
    }
    if (isBoardEmpty(getCurrentSnapshot()) && !selectedIdRef.current) {
      setHasUnsavedChanges(false);
      hasUnsavedChangesRef.current = false;
      return true;
    }
    if (!hasUnsavedChangesRef.current) {
      return true;
    }
    return persistWhiteboard({ source, allowCreate: !selectedIdRef.current });
  }, [clearPendingAutosave, getCurrentSnapshot, isPublicShare, persistWhiteboard]);

  const handleCanvasInteractionChange = useCallback((isInteracting) => {
    if (!isPublicShare && !hasHydratedInitialBoardRef.current) return;
    isCanvasInteractingRef.current = Boolean(isInteracting);
    if (isInteracting) {
      recentSyncActivityUntilRef.current = Date.now() + RECENT_ACTIVITY_SYNC_WINDOW_MS;
      clearPendingAutosave();
      return;
    }

    if (hasUnsavedChangesRef.current) {
      needsResaveRef.current = false;
      clearPendingAutosave();
      pendingSaveTimeoutRef.current = setTimeout(() => {
        pendingSaveTimeoutRef.current = null;
        void persistWhiteboard({
          source: 'autosave',
          allowCreate: !selectedIdRef.current
        });
      }, IMMEDIATE_AUTOSAVE_DELAY_MS);
      return;
    }

    window.setTimeout(() => {
      if (!hasUnsavedChangesRef.current) {
        applyPendingRemoteIfReadyRef.current?.();
      }
    }, 0);
  }, [clearPendingAutosave, isPublicShare, persistWhiteboard]);

  const openSwitchActionModal = useCallback((actionType, targetId = null) => {
    setSwitchActionModal({
      isOpen: true,
      actionType,
      targetId
    });
  }, []);

  const closeSwitchActionModal = useCallback(() => {
    setSwitchActionModal({
      isOpen: false,
      actionType: null,
      targetId: null
    });
    setIsResolvingSwitchAction(false);
  }, []);

  const resetUnsavedAndErrors = useCallback(() => {
    setHasUnsavedChanges(false);
    hasUnsavedChangesRef.current = false;
    setLastSaveError(null);
  }, []);

  const markInitialBoardHydrated = useCallback(() => {
    hasHydratedInitialBoardRef.current = true;
    setHasHydratedInitialBoard(true);
  }, []);

  const loadWhiteboardById = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const response = await apiFetch(`/api/whiteboards/${id}`);
      if (response.ok) {
        const data = await response.json();
        const nextSnapshot = data.whiteboard?.snapshot || EMPTY_SNAPSHOT;
        const nextRevision = Number(data.whiteboard?.revision || 1);
        const fallbackTitle = data.whiteboard?.title?.trim()
          ? data.whiteboard.title
          : formatDefaultWhiteboardTitle(data.whiteboard?.created_at ? new Date(data.whiteboard.created_at) : new Date());
        suppressCanvasChangesUntilRef.current = Date.now() + HYDRATION_SUPPRESS_MS;
        setSelectedId(id);
        selectedIdRef.current = id;
        writeCachedWhiteboardId(id);
        setCurrentSnapshot(nextSnapshot);
        setCurrentRevision(nextRevision);
        currentRevisionRef.current = nextRevision;
        setCurrentTitle(fallbackTitle);
        currentTitleRef.current = fallbackTitle;
        resetUnsavedAndErrors();
        lastSavedSnapshotRef.current = snapshotToString(nextSnapshot);
        lastSavedTitleRef.current = fallbackTitle;
        boardFallbackTitleRef.current = fallbackTitle;
      }
    } catch (err) {
      console.error('Error loading whiteboard:', err);
    } finally {
      setIsLoading(false);
    }
  }, [resetUnsavedAndErrors]);

  const loadPublicSharedWhiteboard = useCallback(async ({ silent = false, showUnavailableOnError = true, returnStatus = false } = {}) => {
    if (!publicShareToken) return returnStatus ? { ok: false, status: null } : false;
    if (!silent) setIsLoading(true);
    try {
      const response = await apiFetch(`/api/public/whiteboards/share/${publicShareToken}`);
      if (!response.ok) {
        const loadError = new Error('Shared whiteboard not found');
        loadError.status = response.status;
        throw loadError;
      }
      const data = await response.json();
      const board = data.whiteboard;
      if (!board?.id) {
        throw new Error('Missing shared whiteboard data');
      }
      const nextSnapshot = board.snapshot || EMPTY_SNAPSHOT;
      const nextRevision = Number(board.revision || 1);
      const nextTitle = board.title?.trim() ? board.title : 'Shared Whiteboard';
      suppressCanvasChangesUntilRef.current = Date.now() + HYDRATION_SUPPRESS_MS;
      setSelectedId(board.id);
      selectedIdRef.current = board.id;
      setCurrentSnapshot(nextSnapshot);
      setCurrentRevision(nextRevision);
      currentRevisionRef.current = nextRevision;
      setCurrentTitle(nextTitle);
      currentTitleRef.current = nextTitle;
      resetUnsavedAndErrors();
      lastSavedSnapshotRef.current = snapshotToString(nextSnapshot);
      lastSavedTitleRef.current = nextTitle;
      boardFallbackTitleRef.current = nextTitle;
      markInitialBoardHydrated();
      setLastSaveError(null);
      return returnStatus ? { ok: true, status: response.status } : true;
    } catch (err) {
      console.error('Error loading shared whiteboard:', err);
      if (showUnavailableOnError) {
        setLastSaveError('Shared whiteboard is unavailable or has been revoked');
      }
      return returnStatus ? { ok: false, status: err?.status || null } : false;
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [markInitialBoardHydrated, publicShareToken, resetUnsavedAndErrors]);

  const applyRemoteWhiteboard = useCallback((remoteBoard, { force = false } = {}) => {
    if (!remoteBoard) return;
    const remoteRevision = Number(remoteBoard.revision || 0);
    if (!force && remoteRevision <= Number(currentRevisionRef.current || 0)) return;
    if (!force && (isCanvasInteractingRef.current || hasUnsavedChangesRef.current || saveInFlightRef.current)) {
      const pendingRevision = Number(pendingRemoteBoardRef.current?.revision || 0);
      if (remoteRevision >= pendingRevision) {
        pendingRemoteBoardRef.current = remoteBoard;
        if (process.env.NODE_ENV === 'development') {
          console.debug('[whiteboard] queued remote snapshot', {
            revision: remoteRevision,
            localRevision: currentRevisionRef.current,
            interacting: isCanvasInteractingRef.current,
            dirty: hasUnsavedChangesRef.current,
            saving: saveInFlightRef.current
          });
        }
      }
      return;
    }
    syncPollingDisabledBoardRef.current = null;
    syncPollBackoffUntilRef.current = 0;
    const localViewport = canvasRef.current?.getSnapshot?.()?.viewport;
    const remoteSnapshot = remoteBoard.snapshot || EMPTY_SNAPSHOT;
    const mergedSnapshot = localViewport
      ? { ...remoteSnapshot, viewport: localViewport }
      : remoteSnapshot;
    const nextTitle = remoteBoard.title?.trim()
      ? remoteBoard.title
      : (currentTitleRef.current || formatDefaultWhiteboardTitle());
    const nextRevision = Number(remoteBoard.revision || currentRevisionRef.current || 1);

    suppressCanvasChangesUntilRef.current = Date.now() + HYDRATION_SUPPRESS_MS;
    setCurrentSnapshot(mergedSnapshot);
    setCurrentTitle(nextTitle);
    currentTitleRef.current = nextTitle;
    setCurrentRevision(nextRevision);
    currentRevisionRef.current = nextRevision;
    lastSavedSnapshotRef.current = snapshotToString(mergedSnapshot);
    lastSavedTitleRef.current = nextTitle;
    boardFallbackTitleRef.current = nextTitle;
    setHasUnsavedChanges(false);
    hasUnsavedChangesRef.current = false;
    setLastSaveError(null);
  }, []);

  const applyPendingRemoteIfReady = useCallback(() => {
    if (isCanvasInteractingRef.current || hasUnsavedChangesRef.current || saveInFlightRef.current) {
      return false;
    }
    const pendingBoard = pendingRemoteBoardRef.current;
    if (!pendingBoard) return false;
    pendingRemoteBoardRef.current = null;
    const pendingRevision = Number(pendingBoard.revision || 0);
    if (pendingRevision <= Number(currentRevisionRef.current || 0)) {
      return false;
    }
    applyRemoteWhiteboard(pendingBoard, { force: true });
    return true;
  }, [applyRemoteWhiteboard]);

  useEffect(() => {
    applyPendingRemoteIfReadyRef.current = applyPendingRemoteIfReady;
  }, [applyPendingRemoteIfReady]);

  // Handle phase change - show wrapup when moving to closing
  const handlePhaseChange = useCallback((newPhase) => {
    setSessionPhase(newPhase);
    if (newPhase === 'closing') {
      setIsWrapupOpen(true);
    }
  }, []);

  const handleOpenWrapup = useCallback(() => {
    handlePhaseChange('closing');
  }, [handlePhaseChange]);

  // Handle session opener suggestion selection
  const handleOpenerSuggestion = useCallback((suggestion) => {
    // Could add the suggestion as a note to the whiteboard
    console.log('Selected opener suggestion:', suggestion);
    setSessionPhase('core'); // Move to core phase after opening
  }, []);

  // Handle context change from WhiteboardInput
  const handleContextChange = useCallback((context) => {
    setInjectedContext(context);
  }, []);

  // Load whiteboards and sessions list on mount
  useEffect(() => {
    if (isPublicShare) {
      void loadPublicSharedWhiteboard();
      return;
    }
    loadWhiteboards();
    loadSessions();
  }, [isPublicShare, loadPublicSharedWhiteboard, loadWhiteboards]);

  useEffect(() => {
    if (isPublicShare) return;
    if (hasHydratedInitialBoardRef.current) return;
    if (!hasLoadedWhiteboards) return;
    if (!Array.isArray(whiteboards)) return;
    if (whiteboards.length === 0) {
      writeCachedWhiteboardId(null);
      setCurrentRevision(0);
      currentRevisionRef.current = 0;
      markInitialBoardHydrated();
      return;
    }
    if (selectedIdRef.current) {
      markInitialBoardHydrated();
      return;
    }

    const requestedBoardId = searchParams.get('board');
    const requestedBoardExists = requestedBoardId
      ? whiteboards.some((board) => String(board.id) === String(requestedBoardId))
      : false;

    if (requestedBoardExists) {
      void loadWhiteboardById(requestedBoardId);
      markInitialBoardHydrated();
      return;
    }

    const cachedBoardId = readCachedWhiteboardId();
    const cachedBoardExists = cachedBoardId
      ? whiteboards.some((board) => String(board.id) === String(cachedBoardId))
      : false;

    if (cachedBoardExists) {
      void loadWhiteboardById(cachedBoardId);
      markInitialBoardHydrated();
      return;
    }

    if (cachedBoardId && !cachedBoardExists) {
      writeCachedWhiteboardId(null);
    }

    const mostRecentBoardId = pickMostRecentBoardId(whiteboards);
    if (mostRecentBoardId) {
      void loadWhiteboardById(mostRecentBoardId);
      markInitialBoardHydrated();
      return;
    }

    markInitialBoardHydrated();
  }, [hasLoadedWhiteboards, isPublicShare, markInitialBoardHydrated, whiteboards, loadWhiteboardById, searchParams]);

  useEffect(() => {
    return () => {
      clearPendingAutosave();
      if (cursorCleanupTimeoutRef.current) {
        window.clearTimeout(cursorCleanupTimeoutRef.current);
        cursorCleanupTimeoutRef.current = null;
      }
      if (cursorEmitRafRef.current != null) {
        window.cancelAnimationFrame(cursorEmitRafRef.current);
        cursorEmitRafRef.current = null;
      }
    };
  }, [clearPendingAutosave]);

  useEffect(() => {
    const cleanup = () => {
      const now = Date.now();
      let didChange = false;
      Object.entries(remoteCursorsRef.current).forEach(([clientId, cursor]) => {
        if (!cursor?.active || now - Number(cursor.updatedAt || 0) > 2500) {
          delete remoteCursorsRef.current[clientId];
          didChange = true;
        }
      });
      if (didChange) {
        setRemoteCursors(Object.values(remoteCursorsRef.current));
      }
      cursorCleanupTimeoutRef.current = window.setTimeout(cleanup, 1200);
    };
    cursorCleanupTimeoutRef.current = window.setTimeout(cleanup, 1200);
    return () => {
      if (cursorCleanupTimeoutRef.current) {
        window.clearTimeout(cursorCleanupTimeoutRef.current);
        cursorCleanupTimeoutRef.current = null;
      }
    };
  }, []);

  const emitCursorUpdate = useCallback((cursor) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected || !selectedIdRef.current) return;
    pendingCursorRef.current = cursor;
    if (cursorEmitRafRef.current != null) return;
    cursorEmitRafRef.current = window.requestAnimationFrame(() => {
      cursorEmitRafRef.current = null;
      const pending = pendingCursorRef.current;
      pendingCursorRef.current = null;
      if (!pending || !socket.connected || !selectedIdRef.current) return;
      socket.emit('whiteboard:cursor', {
        board_id: selectedIdRef.current,
        client_id: whiteboardClientIdRef.current,
        display_label: displayLabelRef.current,
        cursor: pending,
      });
    });
  }, []);

  useEffect(() => {
    const token = isPublicShare ? publicShareToken : getAuthToken();
    if (!token) {
      setIsSocketConnected(false);
      return undefined;
    }

    const socket = io(resolveSocketBaseUrl(), {
      transports: ['websocket', 'polling'],
      auth: { token: isPublicShare ? token : `Bearer ${token}` },
      timeout: 10000,
      withCredentials: true,
    });

    socketRef.current = socket;
    const clientId = whiteboardClientIdRef.current;

    const handleConnect = () => {
      setIsSocketConnected(true);
      socketDisconnectedAtRef.current = 0;
      syncPollingDisabledBoardRef.current = null;
      if (process.env.NODE_ENV === 'development') {
        console.debug('[whiteboard] socket connected');
      }
      const boardId = selectedIdRef.current;
      if (boardId) {
        socket.emit('whiteboard:join', {
          board_id: boardId,
          client_id: clientId,
          display_label: displayLabelRef.current,
        });
        joinedBoardRef.current = boardId;
      }
    };

    const handleDisconnect = () => {
      setIsSocketConnected(false);
      socketDisconnectedAtRef.current = Date.now();
      if (process.env.NODE_ENV === 'development') {
        console.debug('[whiteboard] socket disconnected');
      }
    };

    const handleConnectError = (err) => {
      setIsSocketConnected(false);
      socketDisconnectedAtRef.current = Date.now();
      if (process.env.NODE_ENV === 'development') {
        console.debug('[whiteboard] socket connect_error', err?.message || err);
      }
    };

    const handleReconnectAttempt = (attempt) => {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[whiteboard] socket reconnect attempt', attempt);
      }
    };

    const handleBoardUpdate = (payload) => {
      const board = payload?.whiteboard;
      if (!board?.id) return;
      if (String(board.id) !== String(selectedIdRef.current)) return;
      if (payload?.client_id && payload.client_id === whiteboardClientIdRef.current) return;
      if (hasUnsavedChangesRef.current) return;
      if (Number(board.revision || 0) <= Number(currentRevisionRef.current || 0)) return;
      applyRemoteWhiteboard(board);
    };

    const handlePresenceUpdate = (payload) => {
      if (String(payload?.board_id || '') !== String(selectedIdRef.current || '')) return;
      const labels = {};
      (payload?.presence || []).forEach((entry) => {
        if (!entry?.client_id || entry.client_id === whiteboardClientIdRef.current) return;
        labels[entry.client_id] = entry.display_label || entry.user_id || 'Guest';
      });
      setCollaboratorLabels(labels);
    };

    const handleCursorUpdate = (payload) => {
      if (String(payload?.board_id || '') !== String(selectedIdRef.current || '')) return;
      const clientId = String(payload?.client_id || '');
      if (!clientId || clientId === whiteboardClientIdRef.current) return;
      const cursor = payload?.cursor || {};
      if (!cursor.active || typeof cursor.x !== 'number' || typeof cursor.y !== 'number') {
        delete remoteCursorsRef.current[clientId];
        setRemoteCursors(Object.values(remoteCursorsRef.current));
        return;
      }
      remoteCursorsRef.current[clientId] = {
        clientId,
        displayLabel: payload.display_label || collaboratorLabelsRef.current[clientId] || 'Guest',
        x: cursor.x,
        y: cursor.y,
        tool: cursor.tool,
        active: Boolean(cursor.active),
        updatedAt: Date.now(),
      };
      setRemoteCursors(Object.values(remoteCursorsRef.current));
    };

    socket.on('connect', handleConnect);
    socket.on('connect_error', handleConnectError);
    socket.on('disconnect', handleDisconnect);
    socket.io?.on?.('reconnect_attempt', handleReconnectAttempt);
    socket.on('whiteboard:snapshot', handleBoardUpdate);
    socket.on('whiteboard:updated', handleBoardUpdate);
    socket.on('whiteboard:presence', handlePresenceUpdate);
    socket.on('whiteboard:cursor', handleCursorUpdate);

    return () => {
      try {
        if (joinedBoardRef.current) {
          socket.emit('whiteboard:leave', {
            board_id: joinedBoardRef.current,
            client_id: clientId,
          });
        }
      } catch {
        // ignore disconnect races
      }
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleConnectError);
      socket.off('disconnect', handleDisconnect);
      socket.io?.off?.('reconnect_attempt', handleReconnectAttempt);
      socket.off('whiteboard:snapshot', handleBoardUpdate);
      socket.off('whiteboard:updated', handleBoardUpdate);
      socket.off('whiteboard:presence', handlePresenceUpdate);
      socket.off('whiteboard:cursor', handleCursorUpdate);
      socket.disconnect();
      socketRef.current = null;
      joinedBoardRef.current = null;
      setIsSocketConnected(false);
    };
  }, [applyRemoteWhiteboard, isPublicShare, publicShareToken]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !socket.connected) return;

    if (joinedBoardRef.current && joinedBoardRef.current !== selectedId) {
      socket.emit('whiteboard:leave', {
        board_id: joinedBoardRef.current,
        client_id: whiteboardClientIdRef.current,
      });
      joinedBoardRef.current = null;
    }

    if (selectedId) {
      socket.emit('whiteboard:join', {
        board_id: selectedId,
        client_id: whiteboardClientIdRef.current,
        display_label: displayLabelRef.current,
      });
      joinedBoardRef.current = selectedId;
    }
  }, [selectedId, isSocketConnected]);

  useEffect(() => {
    if (!selectedId) return undefined;
    if (isSocketConnected) return undefined;
    syncPollingDisabledBoardRef.current = null;

    let cancelled = false;
    let timeoutId = null;

    const pollRemoteChanges = async () => {
      if (cancelled || syncPollInFlightRef.current) return;
      syncPollInFlightRef.current = true;
      try {
        const boardId = selectedIdRef.current;
        if (!boardId) return;
        if (syncPollingDisabledBoardRef.current === boardId) return;
        if (socketDisconnectedAtRef.current && Date.now() - socketDisconnectedAtRef.current < SOCKET_FALLBACK_GRACE_MS) {
          return;
        }
        const pollPath = isPublicShare
          ? `/api/public/whiteboards/share/${publicShareToken}?since_revision=${currentRevisionRef.current || 0}`
          : `/api/whiteboards/${boardId}?since_revision=${currentRevisionRef.current || 0}`;
        if (Date.now() < syncPollBackoffUntilRef.current) return;
        const response = await apiFetch(pollPath);
        if (response.status === 429) {
          const retryAfterSeconds = Number(response.headers.get('Retry-After') || 0);
          const retryAfterMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
            ? retryAfterSeconds * 1000
            : DEFAULT_RATE_LIMIT_BACKOFF_MS;
          syncPollBackoffUntilRef.current = Date.now() + retryAfterMs;
          return;
        }
        if (response.status === 404) {
          syncPollingDisabledBoardRef.current = boardId;
          syncPollBackoffUntilRef.current = isPublicShare
            ? Date.now() + DEFAULT_RATE_LIMIT_BACKOFF_MS
            : Number.POSITIVE_INFINITY;
          pendingRemoteBoardRef.current = null;
          if (process.env.NODE_ENV === 'development') {
            console.debug('[whiteboard] disabled polling after 404', { boardId, publicShare: isPublicShare });
          }
          if (isPublicShare) {
            const reloadResult = await loadPublicSharedWhiteboard({
              silent: true,
              showUnavailableOnError: false,
              returnStatus: true
            });
            if (reloadResult.ok) {
              syncPollingDisabledBoardRef.current = null;
              syncPollBackoffUntilRef.current = 0;
              return;
            }
            if (reloadResult.status === 404) {
              setLastSaveError('Shared whiteboard is unavailable or has been revoked');
              cancelled = true;
              return;
            }
            syncPollingDisabledBoardRef.current = null;
            syncPollBackoffUntilRef.current = Date.now() + DEFAULT_RATE_LIMIT_BACKOFF_MS;
            return;
          } else {
            setLastSaveError('Whiteboard sync stopped because this board was not found');
            writeCachedWhiteboardId(null);
            await loadWhiteboards();
          }
          cancelled = true;
          return;
        }
        if (!response.ok) return;
        syncPollBackoffUntilRef.current = 0;
        const data = await response.json();
        if (data?.unchanged) return;
        const remoteBoard = data?.whiteboard;
        if (!remoteBoard?.id) return;
        if (String(remoteBoard.id) !== String(selectedIdRef.current)) return;

        if (hasUnsavedChangesRef.current || isCanvasInteractingRef.current || saveInFlightRef.current) {
          const remoteRevision = Number(remoteBoard.revision || 0);
          const pendingRevision = Number(pendingRemoteBoardRef.current?.revision || 0);
          if (remoteRevision > Number(currentRevisionRef.current || 0) && remoteRevision >= pendingRevision) {
            pendingRemoteBoardRef.current = remoteBoard;
          }
          await persistWhiteboard({ source: 'autosave', allowCreate: false });
          return;
        }

        applyRemoteWhiteboard(remoteBoard);
      } catch (err) {
        console.error('Error polling whiteboard sync:', err);
      } finally {
        syncPollInFlightRef.current = false;
        if (!cancelled) {
          const nextDelay = Date.now() < recentSyncActivityUntilRef.current
            ? ACTIVE_SYNC_POLL_INTERVAL_MS
            : IDLE_SYNC_POLL_INTERVAL_MS;
          timeoutId = window.setTimeout(() => {
            void pollRemoteChanges();
          }, nextDelay);
        }
      }
    };

    void pollRemoteChanges();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [applyRemoteWhiteboard, isPublicShare, isSocketConnected, loadPublicSharedWhiteboard, loadWhiteboards, persistWhiteboard, publicShareToken, selectedId]);

  const loadSessions = async () => {
    try {
      const response = await apiFetch('/api/sessions/list');
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  const handleSessionSelect = async (sessionId) => {
    if (!sessionId) {
      setSelectedSession(null);
      return;
    }
    try {
      const response = await apiFetch(`/api/sessions/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedSession(data);
      }
    } catch (err) {
      console.error('Error loading session:', err);
    }
  };

  const handleSelectWhiteboard = async (id) => {
    if (selectedIdRef.current === id) return;
    clearPendingAutosave();

    if (hasUnsavedChangesRef.current) {
      const saved = await flushPendingSaveIfNeeded('manual');
      if (!saved) {
        openSwitchActionModal('switch', id);
        return;
      }
    }
    await loadWhiteboardById(id);
  };

  const createWhiteboardFromTitle = async (title) => {
    const normalizedTitle = (title || '').trim() || formatDefaultWhiteboardTitle();
    const response = await apiFetch('/api/whiteboards', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: normalizedTitle,
        snapshot: EMPTY_SNAPSHOT,
        client_id: whiteboardClientIdRef.current
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create new whiteboard');
    }

    const data = await response.json();
    const newBoard = data.whiteboard;
    if (!newBoard?.id) {
      throw new Error('Missing whiteboard id in response');
    }

    const nextSnapshot = newBoard.snapshot || EMPTY_SNAPSHOT;
    const nextRevision = Number(newBoard.revision || 1);
    const nextTitle = newBoard.title?.trim()
      ? newBoard.title
      : normalizedTitle;
    suppressCanvasChangesUntilRef.current = Date.now() + HYDRATION_SUPPRESS_MS;
    setSelectedId(newBoard.id);
    selectedIdRef.current = newBoard.id;
    writeCachedWhiteboardId(newBoard.id);
    setCurrentSnapshot(nextSnapshot);
    setCurrentRevision(nextRevision);
    currentRevisionRef.current = nextRevision;
    setCurrentTitle(nextTitle);
    currentTitleRef.current = nextTitle;
    resetUnsavedAndErrors();
    lastSavedSnapshotRef.current = snapshotToString(nextSnapshot);
    lastSavedTitleRef.current = nextTitle;
    boardFallbackTitleRef.current = nextTitle;
    await loadWhiteboards();
    return newBoard;
  };

  const openNewWhiteboardModal = useCallback(() => {
    setNewWhiteboardModal({
      isOpen: true,
      title: formatDefaultWhiteboardTitle()
    });
  }, []);

  const closeNewWhiteboardModal = useCallback(() => {
    if (isCreatingWhiteboard) return;
    setNewWhiteboardModal({
      isOpen: false,
      title: ''
    });
  }, [isCreatingWhiteboard]);

  const handleNewWhiteboard = async () => {
    clearPendingAutosave();

    if (hasUnsavedChangesRef.current) {
      const saved = await flushPendingSaveIfNeeded('manual');
      if (!saved) {
        openSwitchActionModal('new');
        return;
      }
    }

    openNewWhiteboardModal();
  };

  const handleConfirmNewWhiteboard = async ({ share = false } = {}) => {
    setIsCreatingWhiteboard(true);
    setIsLoading(true);
    setShareStatusText('');
    try {
      const newBoard = await createWhiteboardFromTitle(newWhiteboardModal.title);
      setNewWhiteboardModal({ isOpen: false, title: '' });
      if (share) {
        await createShareLinkForBoard(newBoard.id);
      }
    } catch (err) {
      console.error('Error creating whiteboard:', err);
      alert('Could not create a new whiteboard. Please try again.');
    } finally {
      setIsLoading(false);
      setIsCreatingWhiteboard(false);
    }
  };

  const handleSave = async () => {
    clearPendingAutosave();
    await persistWhiteboard({
      source: 'manual',
      allowCreate: true,
      refreshList: true
    });
  };

  const buildAbsoluteShareUrl = useCallback((shareUrl) => {
    if (!shareUrl) return '';
    if (/^https?:\/\//i.test(shareUrl)) return shareUrl;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}${shareUrl.startsWith('/') ? shareUrl : `/${shareUrl}`}`;
  }, []);

  const loadShareStatus = useCallback(async (boardId = selectedIdRef.current) => {
    if (isPublicShare || !boardId) {
      setShareInfo(null);
      return;
    }
    try {
      const response = await apiFetch(`/api/whiteboards/${boardId}/share`);
      if (!response.ok) return;
      const data = await response.json();
      setShareInfo(data.share || null);
    } catch (err) {
      console.error('Error loading share status:', err);
    }
  }, [isPublicShare]);

  const createShareLinkForBoard = async (boardId) => {
    if (!boardId) return;
    setIsShareBusy(true);
    setShareStatusText('');
    try {
      const response = await apiFetch(`/api/whiteboards/${boardId}/share`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error('Failed to create share link');
      }
      const data = await response.json();
      setShareInfo(data.share || null);
      const absoluteUrl = buildAbsoluteShareUrl(data.share_url);
      if (absoluteUrl && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(absoluteUrl);
        setShareStatusText('Link copied');
      } else {
        setShareStatusText('Link ready');
      }
    } catch (err) {
      console.error('Error creating share link:', err);
      setShareStatusText('Share failed');
    } finally {
      setIsShareBusy(false);
    }
  };

  const handleCreateShareLink = async () => {
    if (!selectedIdRef.current) {
      const saved = await persistWhiteboard({ source: 'manual', allowCreate: true, refreshList: true });
      if (!saved || !selectedIdRef.current) return;
    }
    await createShareLinkForBoard(selectedIdRef.current);
  };

  const handleCopyShareLink = async () => {
    if (!shareInfo?.token) return;
    const absoluteUrl = buildAbsoluteShareUrl(`/share/whiteboard/${shareInfo.token}`);
    try {
      await navigator.clipboard?.writeText(absoluteUrl);
      setShareStatusText('Link copied');
    } catch (err) {
      console.error('Error copying share link:', err);
      setShareStatusText(absoluteUrl);
    }
  };

  const handleRevokeShareLink = async () => {
    if (!selectedIdRef.current) return;
    setIsShareBusy(true);
    setShareStatusText('');
    try {
      const response = await apiFetch(`/api/whiteboards/${selectedIdRef.current}/share`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to revoke share link');
      }
      setShareInfo(null);
      setShareStatusText('Link revoked');
    } catch (err) {
      console.error('Error revoking share link:', err);
      setShareStatusText('Revoke failed');
    } finally {
      setIsShareBusy(false);
    }
  };

  useEffect(() => {
    setShareStatusText('');
    if (!isPublicShare) {
      void loadShareStatus(selectedId);
    }
  }, [isPublicShare, loadShareStatus, selectedId]);

  const handleDelete = async (id) => {
    setConfirmDeleteModal({ isOpen: true, boardId: id });
  };

  const handleRenameWhiteboard = async (id, title) => {
    const normalizedTitle = (title || '').trim();
    if (!normalizedTitle) return false;
    try {
      const response = await apiFetch(`/api/whiteboards/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: normalizedTitle })
      });
      if (!response.ok) {
        throw new Error('Failed to rename whiteboard');
      }
      const data = await response.json();
      const savedTitle = data.whiteboard?.title || normalizedTitle;

      setWhiteboards(prev => prev.map(wb => (
        wb.id === id ? { ...wb, title: savedTitle } : wb
      )));

      if (selectedIdRef.current === id) {
        setCurrentTitle(savedTitle);
        currentTitleRef.current = savedTitle;
        lastSavedTitleRef.current = savedTitle;
        boardFallbackTitleRef.current = savedTitle;
      }
      return true;
    } catch (err) {
      console.error('Error renaming whiteboard:', err);
      alert('Could not rename whiteboard. Please try again.');
      return false;
    }
  };

  const closeDeleteModal = useCallback(() => {
    setConfirmDeleteModal({ isOpen: false, boardId: null });
  }, []);

  const confirmDeleteWhiteboard = async () => {
    const id = confirmDeleteModal.boardId;
    if (!id) return;
    closeDeleteModal();
    try {
      const cachedBoardId = readCachedWhiteboardId();
      if (selectedIdRef.current === id) {
        clearPendingAutosave();
      }
      const response = await apiFetch(`/api/whiteboards/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        if (cachedBoardId && String(cachedBoardId) === String(id)) {
          writeCachedWhiteboardId(null);
        }
        if (selectedId === id) {
          resetUnsavedAndErrors();
          await handleNewWhiteboard();
        }
        await loadWhiteboards();
      }
    } catch (err) {
      console.error('Error deleting whiteboard:', err);
    }
  };

  const proceedModalActionWithoutSaving = async () => {
    const { actionType, targetId } = switchActionModal;
    closeSwitchActionModal();
    resetUnsavedAndErrors();

    if (actionType === 'switch' && targetId) {
      await loadWhiteboardById(targetId);
      return;
    }
    if (actionType === 'new') {
      await handleNewWhiteboard();
    }
  };

  const retryModalActionWithSave = async () => {
    setIsResolvingSwitchAction(true);
    const { actionType, targetId } = switchActionModal;
    const saved = await flushPendingSaveIfNeeded('manual');
    if (!saved) {
      setIsResolvingSwitchAction(false);
      return;
    }

    closeSwitchActionModal();
    if (actionType === 'switch' && targetId) {
      await loadWhiteboardById(targetId);
      return;
    }
    if (actionType === 'new') {
      await handleNewWhiteboard();
    }
  };

  const handleExport = async (format) => {
    if (!canvasRef.current) return;

    try {
      await flushPendingSaveIfNeeded();
      if (format === 'svg') {
        const svg = canvasRef.current.exportToSvg();
        if (svg) {
          const svgString = new XMLSerializer().serializeToString(svg);
          const blob = new Blob([svgString], { type: 'image/svg+xml' });
          downloadBlob(blob, `${currentTitle}.svg`);
        }
      } else if (format === 'png') {
        const pngBlob = await canvasRef.current.exportToPng();
        if (pngBlob) {
          downloadBlob(pngBlob, `${currentTitle}.png`);
        }
      } else if (format === 'pdf') {
        const pdf = await canvasRef.current.exportToPdf();
        if (pdf) {
          pdf.save(`${currentTitle}.pdf`);
        }
      } else if (format === 'json') {
        const snapshot = canvasRef.current.getSnapshot();
        const blob = new Blob([snapshotToString(snapshot, 2)], { type: 'application/json' });
        downloadBlob(blob, `${currentTitle}.json`);
      }
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Export failed. Please try again.');
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleEditorChange = useCallback((changeType = 'change') => {
    markDirty(changeType);
  }, [markDirty]);

  // Handle content generation from sessions panel
  const handleGenerateContent = useCallback(async (sessionId, sessionData) => {
    if (!canvasRef.current) {
      console.error('Canvas not ready');
      return;
    }

    setIsGeneratingContent(true);
    setGeneratingSessionId(sessionId);

    try {
      const response = await apiAIFetch('/api/whiteboard/generate-content', 'whiteboard.generate_content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          content_types: ['practice_problems', 'step_by_step_guides'],
          count: 3
        })
      });

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.generated_content) {
          // Add generated elements to the canvas
          data.generated_content.forEach((item, index) => {
            // Small delay between additions to allow positioning
            setTimeout(() => {
              if (item.type === 'note') {
                canvasRef.current.addNote(item.content, item.color || 'yellow');
              } else if (item.type === 'math') {
                canvasRef.current.addMath(item.expression);
              }
            }, index * 100);
          });

          markDirty();
        }
      } else {
        const errorData = await response.json();
        console.error('Content generation failed:', errorData.error || 'Unknown error');
        alert('Failed to generate content. Please try again.');
      }
    } catch (err) {
      console.error('Error generating content:', err);
      alert('Failed to generate content. Please try again.');
    } finally {
      setIsGeneratingContent(false);
      setGeneratingSessionId(null);
    }
  }, [markDirty]);

  const isSaveBusy = isSaving || isAutosaving;
  const isInitialBoardHydrating = !isPublicShare && !hasHydratedInitialBoard;
  const saveStatusText = lastSaveError
    ? lastSaveError
    : (isSaveBusy
        ? 'Saving...'
        : (selectedId && !hasUnsavedChanges ? 'All changes saved' : ''));

  return (
    <div className={`whiteboard-page ${isPublicShare ? 'whiteboard-page--public-share' : ''}`}>
      {!isPublicShare && (
        <button
          type="button"
          className="mobile-whiteboard-settings-btn"
          onClick={() => setIsSidebarOpen((prev) => !prev)}
          aria-label={isSidebarOpen ? 'Close whiteboard settings' : 'Open whiteboard settings'}
          title={isSidebarOpen ? 'Close whiteboard settings' : 'Open whiteboard settings'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M6 12h12M10 18h4" />
          </svg>
        </button>
      )}
      {!isPublicShare && isSidebarOpen && (
        <button
          type="button"
          className="whiteboard-sidebar-backdrop"
          aria-label="Close whiteboard sidebar"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <div className="whiteboard-container">
        {!isPublicShare && (
          <WhiteboardSidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            whiteboards={whiteboards}
            selectedId={selectedId}
            onSelect={handleSelectWhiteboard}
            onNew={handleNewWhiteboard}
            onRename={handleRenameWhiteboard}
            onDelete={handleDelete}
            onGenerateContent={handleGenerateContent}
            isGenerating={isGeneratingContent}
            generatingSessionId={generatingSessionId}
          />
        )}
        <div className="whiteboard-main">
        {/* Header Toggle Button - Always Visible */}
        {!isPublicShare && (
          <button
            className={`whiteboard-header-toggle ${isHeaderCollapsed ? '' : 'whiteboard-header-toggle--expanded'}`}
            onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
            title={isHeaderCollapsed ? 'Show toolbar' : 'Hide toolbar'}
            aria-expanded={!isHeaderCollapsed}
          >
            {isHeaderCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        )}

        {/* Collapsible Header Toolbar */}
        <div className={`whiteboard-toolbar ${isHeaderCollapsed ? 'whiteboard-toolbar--collapsed' : ''}`}>
          <div className="whiteboard-title-section">
            {isPublicShare ? (
              <div className="whiteboard-public-title">{currentTitle || 'Shared Whiteboard'}</div>
            ) : (
              <>
                <input
                  type="text"
                  className="whiteboard-title-input"
                  value={currentTitle}
                  onChange={(e) => {
                    setCurrentTitle(e.target.value);
                    currentTitleRef.current = e.target.value;
                    markDirty();
                  }}
                  placeholder="Whiteboard title..."
                />
                <HelperIcon
                  title={helperContent.whiteboardAI.title}
                  content={helperContent.whiteboardAI.content}
                  size="small"
                />
                <button
                  type="button"
                  className="whiteboard-wrapup-btn"
                  onClick={handleOpenWrapup}
                  aria-label="Open Session Wrap-up"
                  title="Session Wrap-up"
                >
                  <Brain size={18} />
                </button>
              </>
            )}
          </div>
          <div className="whiteboard-toolbar-actions">
            {!isPublicShare && (
              <>
                <button
                  type="button"
                  className="whiteboard-inline-menu-btn"
                  onClick={() => setIsSidebarOpen(true)}
                  aria-label="Open whiteboard sidebar"
                >
                  Whiteboards
                </button>
                <select
                  className="session-selector"
                  value={selectedSession?.id || ''}
                  onChange={(e) => handleSessionSelect(e.target.value)}
                >
                  <option value="">No session attached</option>
                  {sessions.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.title || s.id}
                    </option>
                  ))}
                </select>
                <button
                  className="lrbrain-btn lrbrain-btn-outline whiteboard-btn"
                  onClick={handleSave}
                  disabled={isSaveBusy}
                >
                  {isSaveBusy ? 'Saving...' : (hasUnsavedChanges ? 'Save *' : 'Save')}
                </button>
                <button
                  type="button"
                  className="lrbrain-btn lrbrain-btn-outline whiteboard-btn"
                  onClick={shareInfo ? handleCopyShareLink : handleCreateShareLink}
                  disabled={isShareBusy}
                >
                  {isShareBusy ? 'Sharing...' : (shareInfo ? 'Copy Share Link' : 'Share')}
                </button>
                {shareInfo && (
                  <button
                    type="button"
                    className="lrbrain-btn lrbrain-btn-outline whiteboard-btn"
                    onClick={handleRevokeShareLink}
                    disabled={isShareBusy}
                  >
                    Revoke
                  </button>
                )}
              </>
            )}
            {saveStatusText && (
              <span className="whiteboard-save-status" aria-live="polite">
                {saveStatusText}
              </span>
            )}
            {shareStatusText && !isPublicShare && (
              <span className="whiteboard-save-status" aria-live="polite">
                {shareStatusText}
              </span>
            )}
            {!isPublicShare && (
              <div className="whiteboard-export-dropdown">
                <button className="lrbrain-btn lrbrain-btn-outline whiteboard-btn">
                  Export
                </button>
                <div className="whiteboard-export-menu">
                  <button onClick={() => handleExport('pdf')}>PDF Document</button>
                  <button onClick={() => handleExport('png')}>PNG Image</button>
                  <button onClick={() => handleExport('svg')}>SVG Vector</button>
                  <button onClick={() => handleExport('json')}>JSON Data</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="whiteboard-canvas-wrapper">
          {isLoading || isInitialBoardHydrating ? (
            <div className="whiteboard-loading">Loading whiteboard...</div>
          ) : (
            <WhiteboardCanvas
              key={selectedId || 'new'}
              snapshot={currentSnapshot}
              syncClientId={whiteboardClientIdRef.current}
              onMount={(api) => {
                canvasRef.current = api;
              }}
              onChange={handleEditorChange}
              onInteractionStateChange={handleCanvasInteractionChange}
              sessionContext={selectedSession}
              injectedContext={injectedContext}
              onContextChange={handleContextChange}
              currentPhase={sessionPhase}
              isPublicShare={isPublicShare}
              remoteCursors={remoteCursors}
              onCursorChange={emitCursorUpdate}
            />
          )}
        </div>
      </div>

      {/* Session Opener Panel - shown at start of session */}
      {!isPublicShare && (
        <SessionOpenerPanel
          isOpen={isOpenerOpen}
          onClose={() => setIsOpenerOpen(false)}
          sessionContext={selectedSession}
          onSelectSuggestion={handleOpenerSuggestion}
        />
      )}

      {/* Metacognitive Wrap-up Panel - shown during closing phase */}
      {!isPublicShare && (
        <MetacognitiveWrapup
          isOpen={isWrapupOpen}
          onClose={() => setIsWrapupOpen(false)}
          sessionContext={selectedSession}
          whiteboardSnapshot={canvasRef.current?.getSnapshot?.()}
          onExport={handleExport}
          studentContext={studentContext}
          whiteboardTitle={currentTitle}
        />
      )}

      {newWhiteboardModal.isOpen && (
        <div className="whiteboard-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="new-whiteboard-title">
          <div className="whiteboard-modal whiteboard-new-modal">
            <h3 className="whiteboard-modal__title" id="new-whiteboard-title">Create New Whiteboard</h3>
            <p className="whiteboard-modal__body">
              Name this board before saving or sharing it.
            </p>
            <label className="whiteboard-new-modal__label" htmlFor="new-whiteboard-title-input">
              Title
            </label>
            <input
              id="new-whiteboard-title-input"
              type="text"
              className="whiteboard-new-modal__input"
              value={newWhiteboardModal.title}
              onChange={(event) => setNewWhiteboardModal((prev) => ({
                ...prev,
                title: event.target.value
              }))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleConfirmNewWhiteboard();
                }
                if (event.key === 'Escape') {
                  closeNewWhiteboardModal();
                }
              }}
              autoFocus
            />
            <div className="whiteboard-modal__actions">
              <button
                type="button"
                className="lrbrain-btn lrbrain-btn-outline"
                onClick={closeNewWhiteboardModal}
                disabled={isCreatingWhiteboard}
              >
                Cancel
              </button>
              <button
                type="button"
                className="lrbrain-btn lrbrain-btn-outline"
                onClick={() => handleConfirmNewWhiteboard({ share: true })}
                disabled={isCreatingWhiteboard}
              >
                {isCreatingWhiteboard ? 'Creating...' : 'Share'}
              </button>
              <button
                type="button"
                className="lrbrain-btn lrbrain-btn-solid"
                onClick={() => handleConfirmNewWhiteboard()}
                disabled={isCreatingWhiteboard}
              >
                {isCreatingWhiteboard ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {switchActionModal.isOpen && (
        <div className="whiteboard-modal-overlay" role="dialog" aria-modal="true">
          <div className="whiteboard-modal">
            <h3 className="whiteboard-modal__title">Couldn&apos;t Save Changes</h3>
            <p className="whiteboard-modal__body">
              We couldn&apos;t save your current whiteboard yet. What would you like to do?
            </p>
            <div className="whiteboard-modal__actions">
              <button
                className="lrbrain-btn lrbrain-btn-outline"
                onClick={closeSwitchActionModal}
                disabled={isResolvingSwitchAction}
              >
                Stay Here
              </button>
              <button
                className="lrbrain-btn lrbrain-btn-outline"
                onClick={proceedModalActionWithoutSaving}
                disabled={isResolvingSwitchAction}
              >
                Leave Anyway
              </button>
              <button
                className="lrbrain-btn lrbrain-btn-solid"
                onClick={retryModalActionWithSave}
                disabled={isResolvingSwitchAction}
              >
                {isResolvingSwitchAction ? 'Retrying...' : 'Retry Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteModal.isOpen && (
        <div className="whiteboard-modal-overlay" role="dialog" aria-modal="true">
          <div className="whiteboard-modal">
            <h3 className="whiteboard-modal__title">Delete Whiteboard?</h3>
            <p className="whiteboard-modal__body">
              This whiteboard will be permanently removed.
            </p>
            <div className="whiteboard-modal__actions">
              <button
                className="lrbrain-btn lrbrain-btn-outline"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                className="lrbrain-btn lrbrain-btn-solid"
                onClick={confirmDeleteWhiteboard}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Whiteboard;
